import { Video, Timer, VolumeX, Users, MessageSquare, Monitor } from "lucide-react";
import { Link } from "react-router-dom";

const roomTypes = [
  {
    id: "pomodoro",
    title: "Pomodoro Rooms",
    description: "Everyone works in synchronized blocks of 25–50 minutes with short breaks. Timed sessions align with our natural attention cycles and prevent burnout.",
    icon: Timer,
    link: "/study-timer/",
  },
  {
    id: "silent",
    title: "Silent Study",
    description: "Cameras stay on but microphones are muted. This mirrors the quiet of a library while still providing visual accountability.",
    icon: VolumeX,
    link: "/study-room/",
  },
  {
    id: "collaborative",
    title: "Collaborative Rooms",
    description: "Talk through problems, brainstorm and share screens. Explaining concepts to others improves understanding and retention.",
    icon: Users,
    link: "/group-study/",
  },
];

export const StudyRooms = () => {
  return (
    <section className="py-20 px-4 relative overflow-hidden" aria-labelledby="study-rooms-title">
      <div className="absolute inset-0 bg-gradient-to-b from-muted/50 via-background to-muted/50" aria-hidden="true" />
      
      <div className="container mx-auto relative z-10">
        <header className="text-center mb-12 animate-fade-in">
          <h2 id="study-rooms-title" className="font-display text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
            Study Rooms: Real-Time Focus Hubs
          </h2>
          <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            CSW's study rooms mimic the library experience by pairing video presence with digital tools. Live video, screen-sharing and chat create what psychologists call <strong>social facilitation</strong> – the tendency to perform better in the presence of others. When you see peers working, you're naturally motivated to stay focused. Study rooms also provide a comfortable place to ask questions and feel connected, reducing the isolation often reported in online classes. And because CSW runs 24/7, you can always find a room to match your subject, time zone or vibe.
          </p>
        </header>

        {/* Room Types */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-center mb-8 text-foreground">Room Types</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {roomTypes.map((room, index) => {
              const Icon = room.icon;
              return (
                <Link
                  key={room.id}
                  to={room.link}
                  className="group p-6 rounded-xl bg-card border-2 border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-bold text-foreground">{room.title}</h4>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{room.description}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};